import React from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  useParams,
} from 'react-router-dom';
import { Outlet, redirect } from 'react-router';
import { Client as Styletron } from 'styletron-engine-atomic';
import { RelayMessengerHome } from '@app/two-way/relay-messenger/components/relay-messenger-home/RelayMessengerHome';
import { RelayMessengerProvider } from '../../context/RelayMessengerProvider';
import { RelayMessengerFeatureFlag } from '../../relay-messenger.component';
import { ConversationErrorBoundary } from '../error-boundary/ConversationErrorBoundary';

export type TwoWayMessagingContainerProps = {
  clientId: string;
  userId: string;
  userRoleIds: string[];
  channelSid: string | null;
  rmTranslationsEnabled: boolean;
  featureFlags: RelayMessengerFeatureFlag[];
};

const engine = new Styletron({ prefix: 'new-two-way-' }); // generate classNames with a prefix of 'new-two-way';

export type ConversationType = 'assigned' | 'unassigned' | 'closed' | 'agent';

export const isConversationType = (
  conversationType?: string,
): conversationType is ConversationType =>
  conversationType === 'assigned' ||
  conversationType === 'unassigned' ||
  conversationType === 'closed' ||
  conversationType === 'agent';

export const TwoWayMessagingContainer = ({
  clientId,
  userId,
  userRoleIds,
  channelSid,
  rmTranslationsEnabled,
  featureFlags,
}: TwoWayMessagingContainerProps) => {
  function RelayMessengerHomeRoute() {
    // firstParam and secondParam are used to capture the dynamic segments of the URL:
    // - for assigned/unassigned/closed conversations (firstParam = channelSid and secondParam = undefined);
    // - for agent conversations (firstParam = agentId and secondParam = channelSid).
    const { conversationType, firstParam, secondParam } = useParams();

    if (!isConversationType(conversationType)) {
      throw new Error(`Invalid conversation type: ${conversationType}`);
    }

    return (
      <RelayMessengerHome
        conversationType={conversationType}
        routeAgentId={conversationType === 'agent' ? firstParam : undefined}
        routeChannelSid={
          conversationType === 'agent' ? secondParam : firstParam
        }
      />
    );
  }

  function RootLayout() {
    return (
      <RelayMessengerProvider
        clientId={clientId}
        userId={userId}
        userRoleIds={userRoleIds}
        channelSid={channelSid}
        rmTranslationsEnabled={rmTranslationsEnabled}
        featureFlags={featureFlags}
      >
        <Outlet />
      </RelayMessengerProvider>
    );
  }

  const router = createBrowserRouter(
    [
      {
        element: <RootLayout />,
        children: [
          {
            index: true,
            loader: () => redirect(`/${clientId}/assigned`),
          },
          {
            path: ':clientId',
            loader: () => redirect(`/${clientId}/assigned`),
          },
          {
            path: ':clientId/:conversationType/:firstParam?/:secondParam?',
            element: <RelayMessengerHomeRoute />,
            ErrorBoundary: () => (
              <ConversationErrorBoundary clientId={clientId} />
            ),
          },
        ],
      },
    ],
    {
      basename: '/two-way/relay-messenger',
    },
  );

  return <RouterProvider router={router} />;
};
